/**
 * Upgrade screen - Click upgrades & Special upgrades
 */

import { CLICK_UPGRADES, SPECIAL_UPGRADES } from '../game/data/upgrades.js';
import { formatNumber } from '../utils/format.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import {
  dispatch, getState, getViewState, setViewState, onRender, setInnerHTML,
} from './renderer.js';

export function initUpgradeScreen() {
  onRender(renderUpgradeScreen);
}

function renderUpgradeScreen() {
  if (getViewState().currentTab !== 'upgrade') return;

  const container = document.getElementById('screen-content');
  if (!container) return;

  const vs = getViewState();

  const html = `
    <div class="upgrade-screen">
      <div class="sub-tabs">
        <button class="sub-tab ${vs.upgradeSubTab === 'click' ? 'active' : ''}" data-subtab="click">${t('subtab_click')}</button>
        <button class="sub-tab ${vs.upgradeSubTab === 'special' ? 'active' : ''}" data-subtab="special">${t('subtab_special')}</button>
      </div>
      <div class="upgrade-list" id="upgrade-list"></div>
    </div>
  `;

  if (setInnerHTML(container, html)) {
    // Sub tab handlers (only rebind when DOM changed)
    container.querySelectorAll('[data-subtab]').forEach(btn => {
      btn.addEventListener('click', () => {
        setViewState({ upgradeSubTab: (btn as HTMLElement).dataset.subtab as 'click' | 'special' });
      });
    });
  }

  if (vs.upgradeSubTab === 'click') {
    renderClickUpgrades();
  } else {
    renderSpecialUpgrades();
  }
}

function renderClickUpgrades() {
  const listEl = document.getElementById('upgrade-list');
  if (!listEl) return;

  const state = getState();

  let html = '';
  CLICK_UPGRADES.forEach((upg, idx) => {
    const bought = state.clickUpgradesBought.includes(idx);
    const unlocked = idx === 0 || state.clickUpgradesBought.includes(idx - 1);
    const canAfford = !bought && unlocked && state.ip >= upg.cost;

    let effectDesc = '';
    if (upg.flatBonus) effectDesc = `+${upg.flatBonus} tap`;
    if (upg.cpsPercent) effectDesc = `+${upg.cpsPercent}% 🛸/s per tap`;
    if (upg.critChance) effectDesc = `CRIT +${Math.round(upg.critChance * 100)}%`;
    if (upg.critMult) effectDesc = `CRIT x${upg.critMult}`;

    if (!unlocked && !bought) {
      // Locked - show as locked
      html += `
        <div class="upgrade-card locked">
          <div class="upgrade-info">
            <div class="upgrade-name" style="color:var(--text-dim)">\u{1F512} ${t(upg.nameKey)}</div>
            <div class="upgrade-desc">${t('upgrade_locked_prev')}</div>
          </div>
        </div>
      `;
    } else {
      html += `
        <div class="upgrade-card ${bought ? 'bought' : ''} ${canAfford ? 'affordable' : ''}">
          <div class="upgrade-info">
            <div class="upgrade-name">${t(upg.nameKey)} ${bought ? '\u2713' : ''}</div>
            <div class="upgrade-desc">${effectDesc}</div>
          </div>
          <div style="text-align:right;">
            <div class="upgrade-cost ip">${formatNumber(upg.cost)}</div>
            ${!bought ? `<button class="buy-btn" ${canAfford ? '' : 'disabled'} data-buy-click="${idx}">${t('buy')}</button>` : ''}
          </div>
        </div>
      `;
    }
  });

  if (setInnerHTML(listEl, html)) {
    listEl.querySelectorAll('[data-buy-click]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = parseInt((btn as HTMLElement).dataset.buyClick!);
        dispatch({ type: 'BUY_CLICK_UPGRADE', payload: { index: idx } });
        playSfx('upgrade');
      });
    });
  }
}

function renderSpecialUpgrades() {
  const listEl = document.getElementById('upgrade-list');
  if (!listEl) return;

  const state = getState();

  let html = '';
  for (const upg of SPECIAL_UPGRADES) {
    const bought = state.specialUpgradesBought.includes(upg.id);
    const canAfford = !bought && state.ip >= upg.cost;

    // Check condition visibility
    let conditionMet = true;
    let condText = '';
    const cond = upg.condition;
    if (cond.type === 'producers') {
      for (const req of cond.requirements) {
        const owned = state.producers[req.producerId]?.count ?? 0;
        if (owned < req.count) conditionMet = false;
      }
      condText = cond.requirements.map(r =>
        `${t('prod_' + r.producerId)} x${r.count}`
      ).join(', ');
    } else if (cond.type === 'combo') {
      if (state.maxCombo < cond.maxCombo) conditionMet = false;
      condText = `Max combo ${cond.maxCombo}+`;
    } else if (cond.type === 'playtime') {
      if (state.totalPlayTimeMs < cond.hours * 3600_000) conditionMet = false;
      condText = `${cond.hours}h playtime`;
    } else if (cond.type === 'totalProducers') {
      const total = Object.values(state.producers).reduce((s, p) => s + p.count, 0);
      if (total < cond.count) conditionMet = false;
      condText = `${cond.count}+ total producers`;
    }

    const effectiveCanAfford = canAfford && conditionMet;

    html += `
      <div class="upgrade-card ${bought ? 'bought' : ''} ${effectiveCanAfford ? 'affordable' : ''}">
        <div class="upgrade-info">
          <div class="upgrade-name">${t(upg.nameKey)} ${bought ? '\u2713' : ''}</div>
          <div class="upgrade-desc">${t(upg.descKey)}</div>
          ${!bought && !conditionMet ? `<div class="upgrade-desc" style="color:var(--warning)">${t('condition')}: ${condText}</div>` : ''}
        </div>
        <div style="text-align:right;">
          <div class="upgrade-cost ip">${formatNumber(upg.cost)}</div>
          ${!bought ? `<button class="buy-btn" ${effectiveCanAfford ? '' : 'disabled'} data-buy-special="${upg.id}">${t('buy')}</button>` : ''}
        </div>
      </div>
    `;
  }

  if (setInnerHTML(listEl, html)) {
    listEl.querySelectorAll('[data-buy-special]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (btn as HTMLElement).dataset.buySpecial!;
        dispatch({ type: 'BUY_SPECIAL_UPGRADE', payload: { upgradeId: id } });
        playSfx('upgrade');
      });
    });
  }
}
